import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Toaster } from "@/components/ui/sonner";
import { Spinner } from "@/components/ui/spinner";
import { environment } from "@/config/env";
import { useEliteStructure } from "@/contexts/EliteTemporaryContextProvider";
import axios from "axios";
import * as LucideIcons from "lucide-react";
import { ArrowLeft, Clock, Globe, Info, MapPin, Phone } from "lucide-react";
import { motion } from "motion/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

export default function PointOfInterestDetail() {
  const routeParams = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { structure } = useEliteStructure();
  const { t, i18n } = useTranslation("common");
  const [poi, setPoi] = useState<any>(null);

  const poiName = poi ? getPoiText(poi.name) : "";
  useDocumentTitle(poiName || "Dettaglio Punto d'Interesse");

  function getPoiText(val: any): string {
    if (!val) return "";
    let translations = val;
    if (typeof val === "string") {
      try {
        translations = JSON.parse(val);
      } catch {
        return val;
      }
    }
    if (typeof translations === "object" && translations !== null) {
      let language = i18n.language === "en" ? "gb" : i18n.language;
      return (
        translations?.[language] ||
        translations?.["it"] ||
        Object.values(translations)[0] ||
        ""
      );
    }
    return String(val);
  }

  const days = [
    { key: "monday", label: t("days.monday") },
    { key: "tuesday", label: t("days.tuesday") },
    { key: "wednesday", label: t("days.wednesday") },
    { key: "thursday", label: t("days.thursday") },
    { key: "friday", label: t("days.friday") },
    { key: "saturday", label: t("days.saturday") },
    { key: "sunday", label: t("days.sunday") },
  ];

  function getServiceCategory(poiId: string) {
    axios.defaults.baseURL = environment.ELITE_BASE_URI;
    axios
      .get(`concierge/${structure.id}/pointofinterest/${poiId}`)
      .then((response) => {
        let poiArr = response.data || [];
        setPoi(poiArr);
      })
      .catch((err) => {
        navigate("/expired", { replace: true });
      });
  }

  useEffect(() => {
    if (structure?.id) {
      const poiId = routeParams.id;

      if (poiId) {
        getServiceCategory(poiId);
      }
    }
  }, [routeParams.id, navigate, structure?.id]);

  if (!poi) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <Spinner className="size-10 text-primary" />
      </div>
    );
  }

  const images: string[] =
    Array.isArray(poi.pointOfInterestImages) &&
    poi.pointOfInterestImages.length > 0
      ? poi.pointOfInterestImages
      : poi.cover
        ? [poi.cover]
        : [];

  const locationString = [poi.address, poi.city].filter(Boolean).join(", ");

  function handleBack() {
    const idx = (window.history.state as { idx?: number } | null)?.idx;
    if (typeof idx === "number" && idx > 0) {
      navigate(-1);
    } else {
      navigate("/point-of-interest", { replace: true });
    }
  }

  function toPascalCase(value: string) {
    return value
      .split(/[-_\s]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join("");
  }

  function getCategoryIcon(iconName?: string) {
    const iconKey = toPascalCase(iconName || "Tag");
    return (LucideIcons as any)[iconKey] ?? LucideIcons.Tag;
  }

  function normalizeBase64Image(value: unknown): string | undefined {
    if (typeof value !== "string") return undefined;
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    if (trimmed.startsWith("data:image/")) return trimmed;
    if (/^[A-Za-z0-9+/=\r\n]+$/.test(trimmed)) {
      return `data:image/png;base64,${trimmed.replace(/\s+/g, "")}`;
    }
    return undefined;
  }

  const categoryText = poi ? getPoiText(poi.categoryName || poi.category) : "";
  const CategoryIcon = getCategoryIcon(poi?.categoryDefaultIcon);
  const customIconSrc = normalizeBase64Image(poi?.categoryCustomIcon);

  return (
    <div className="min-h-screen pb-16 pt-2 px-3 sm:px-6 lg:px-8 max-w-6xl mx-auto">
      <div className="mb-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBack}
          className="gap-2 text-muted-foreground hover:text-foreground pl-0"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("back") !== "back" ? t("back") : "Indietro"}
        </Button>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="mb-6 space-y-2">
          {categoryText && (
            <Badge
              variant="secondary"
              className="text-xs w-fit flex items-center gap-1.5 px-2.5 py-1"
            >
              {customIconSrc ? (
                <img
                  src={customIconSrc}
                  alt="Category icon"
                  className="h-3.5 w-3.5 object-cover rounded-sm shrink-0"
                />
              ) : (
                <CategoryIcon className="h-3.5 w-3.5 text-primary shrink-0" />
              )}
              <span>{categoryText}</span>
            </Badge>
          )}

          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground break-words [overflow-wrap:anywhere]">
            {getPoiText(poi.name)}
          </h1>

          {locationString && (
            <p className="text-sm text-muted-foreground flex items-center gap-1.5">
              <MapPin className="h-4 w-4 text-primary shrink-0" />
              {locationString}
            </p>
          )}
        </div>

        {images.length > 0 && (
          <div className="mb-8 rounded-2xl overflow-hidden shadow-sm border bg-card">
            <Carousel
              autoPlay={true}
              opts={{ loop: true }}
              className="w-full relative"
            >
              <CarouselContent>
                {images.map((src: any, i: number) => (
                  <CarouselItem key={i}>
                    <div className="w-full h-56 sm:h-72 md:h-96">
                      <img
                        src={typeof src === "string" ? src : src.image}
                        alt={`${getPoiText(poi.name)} ${i + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              {images.length > 1 && (
                <>
                  <CarouselPrevious className="left-3 bg-background/80 backdrop-blur-sm" />
                  <CarouselNext className="right-3 bg-background/80 backdrop-blur-sm" />
                </>
              )}
            </Carousel>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start min-w-0">
          <div className="lg:col-span-2 space-y-8 min-w-0">
            {poi.description && (
              <Card className="p-6 shadow-sm border border-border/60 overflow-hidden min-w-0">
                <h2 className="text-lg font-semibold mb-3 flex items-center gap-2 text-foreground">
                  <Info className="h-5 w-5 text-primary shrink-0" />
                  {t("description") || "Descrizione"}
                </h2>
                <div
                  className="prose dark:prose-invert max-w-none text-muted-foreground leading-relaxed text-sm md:text-base break-words [overflow-wrap:anywhere] [word-break:break-word] whitespace-normal overflow-hidden"
                  dangerouslySetInnerHTML={{
                    __html: getPoiText(poi.description),
                  }}
                />
              </Card>
            )}
          </div>

          <div className="space-y-6 min-w-0">
            <Card className="p-6 space-y-5 border border-border/60 shadow-sm min-w-0">
              <h3 className="font-semibold text-base border-b pb-3 flex items-center gap-2 text-foreground">
                <Info className="h-4 w-4 text-primary shrink-0" />
                {t("info_and_hours") !== "info_and_hours"
                  ? t("info_and_hours")
                  : "Informazioni e Orari"}
              </h3>

              {locationString && (
                <div className="flex items-start gap-3 text-sm">
                  <MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground text-xs uppercase tracking-wider text-muted-foreground">
                      {t("location") || "Posizione"}
                    </p>
                    <p className="text-foreground mt-0.5">{locationString}</p>
                  </div>
                </div>
              )}

              {poi.pointOfInterestLevelPrice != null && (
                <div className="flex items-center justify-between text-sm pt-1">
                  <span className="text-muted-foreground">
                    {t("price_level") || "Fascia prezzo"}:
                  </span>
                  <span className="font-semibold tracking-wider">
                    {(() => {
                      const level = Number(poi.pointOfInterestLevelPrice) + 1;
                      const filled = "€".repeat(
                        Math.min(Math.max(level, 1), 4),
                      );
                      const empty = "€".repeat(4 - filled.length);
                      const isExpensive = level >= 4;
                      return (
                        <>
                          <span
                            className={
                              isExpensive
                                ? "text-amber-500"
                                : "text-green-600 dark:text-green-400"
                            }
                          >
                            {filled}
                          </span>
                          <span className="text-muted-foreground/30">
                            {empty}
                          </span>
                        </>
                      );
                    })()}
                  </span>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-1">
                {poi.website && (
                  <Button asChild variant="outline" size="sm" className="gap-2">
                    <a
                      href={
                        poi.website.startsWith("http")
                          ? poi.website
                          : `https://${poi.website}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Globe className="h-4 w-4" />
                      {t("website")}
                    </a>
                  </Button>
                )}
                {poi.tel && (
                  <Button asChild variant="outline" size="sm" className="gap-2">
                    <a href={`tel:${poi.tel}`}>
                      <Phone className="h-4 w-4" />
                      {t("call")}
                    </a>
                  </Button>
                )}
              </div>

              {poi.serviceAvailabilityData &&
                poi.serviceAvailabilityData !== "" &&
                (() => {
                  try {
                    const slot = JSON.parse(poi.serviceAvailabilityData)[0];
                    if (!slot) return null;

                    return (
                      <div className="space-y-3 pt-2">
                        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          <Clock className="h-3.5 w-3.5 text-primary shrink-0" />
                          <span>
                            {t("opening_hours") !== "OPENING_HOURS" &&
                            t("opening_hours") !== "opening_hours"
                              ? t("opening_hours")
                              : "Orari di apertura"}
                          </span>
                        </div>
                        <div className="divide-y divide-border/40 text-sm">
                          {days.map(({ key, label }) => {
                            const isOpen =
                              slot[key] === true ||
                              poi.serviceAvailability == 0;
                            const t1s = slot[`${key}StartTime`];
                            const t1e = slot[`${key}EndTime`];
                            const t2s = slot[`${key}SecondStartTime`];
                            const t2e = slot[`${key}SecondEndTime`];
                            const slot1 = t1s && t1e ? `${t1s} – ${t1e}` : null;
                            const slot2 = t2s && t2e ? `${t2s} – ${t2e}` : null;

                            return (
                              <div
                                key={key}
                                className="py-2 flex items-center justify-between gap-2"
                              >
                                <span className="font-medium text-foreground text-xs sm:text-sm">
                                  {label}
                                </span>
                                <div className="text-right">
                                  {!isOpen ? (
                                    <Badge
                                      variant="outline"
                                      className="text-xs bg-destructive/10 text-destructive border-destructive/20 font-normal"
                                    >
                                      {t("close") || "Chiuso"}
                                    </Badge>
                                  ) : slot1 ? (
                                    <span className="text-xs text-muted-foreground font-mono">
                                      {slot1}
                                      {slot2 ? ` / ${slot2}` : ""}
                                    </span>
                                  ) : (
                                    <Badge
                                      variant="outline"
                                      className="text-xs bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 font-normal"
                                    >
                                      {t("open") || "Aperto"}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  } catch {
                    return null;
                  }
                })()}
            </Card>
          </div>
        </div>

        {(poi.latitude || poi.longitude) && (
          <div className="mt-10">
            <iframe
              className="rounded-md w-full"
              height="400"
              src={`https://maps.google.com/maps?q=${poi.latitude ?? poi.city},${poi.longitude ?? poi.address}&z=15&output=embed`}
            />
          </div>
        )}
      </motion.div>

      <Toaster />
    </div>
  );
}
